import { Controller } from "@hotwired/stimulus";

const MINUTE = 60 * 1000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

// Time left in words a person reads at a glance: "5 days 2 h", "3 h 12 min", "14 min", "40 s"
export function formatTimeLeft(ms: number): string {
  if (ms >= DAY) {
    const days = Math.floor(ms / DAY);
    const hours = Math.floor((ms % DAY) / HOUR);
    const dayWord = days === 1 ? "day" : "days";
    return hours > 0 ? `${days} ${dayWord} ${hours} h` : `${days} ${dayWord}`;
  }
  if (ms >= HOUR) {
    const hours = Math.floor(ms / HOUR);
    const minutes = Math.floor((ms % HOUR) / MINUTE);
    return minutes > 0 ? `${hours} h ${minutes} min` : `${hours} h`;
  }
  if (ms >= MINUTE) return `${Math.floor(ms / MINUTE)} min`;
  return `${Math.max(0, Math.ceil(ms / 1000))} s`;
}

// Counts down to an ISO time (data-countdown-target-value) and reloads the page when it passes
export default class extends Controller<HTMLElement> {
  static values = {
    target: String,
  };

  declare targetValue: string;
  private intervalId: number | null = null;

  connect(): void {
    this.updateCountdown();
    this.intervalId = window.setInterval(() => this.updateCountdown(), 1000);
  }

  disconnect(): void {
    this.stop();
  }

  updateCountdown(): void {
    const diff = new Date(this.targetValue).getTime() - Date.now();

    if (diff <= 0) {
      this.element.textContent = formatTimeLeft(0);
      this.stop();
      window.location.reload();
      return;
    }

    const text = formatTimeLeft(diff);
    if (this.element.textContent !== text) this.element.textContent = text;
  }

  private stop(): void {
    if (this.intervalId !== null) {
      window.clearInterval(this.intervalId);
      this.intervalId = null;
    }
  }
}
